import React, { useState, useRef, useEffect } from "react";
import { colorTokens, getSpacing, typographyTokens, getTypography } from "../../../../designTokens";

interface DatePickerCalendarProps {
  isOpen: boolean;
  onClose: () => void;
  onDateSelect: (date: string) => void;
  selectedDate?: string;
  idPrefix?: string;
  inline?: boolean;
  /** When true, past dates are selectable (e.g. for export date range). Default false = only today and future. */
  allowPastDates?: boolean;
  /** When true, future dates (after today) are not selectable. */
  disableFutureDates?: boolean;
  /** Optional minimum selectable date (same format as selectedDate, e.g. "6 March 2025"). Dates before this are disabled. */
  minDate?: string;
  /** Optional maximum selectable date (same format as selectedDate). Dates after this are disabled. */
  maxDate?: string;
}

const DatePickerCalendar: React.FC<DatePickerCalendarProps> = ({
  isOpen,
  onClose,
  onDateSelect,
  selectedDate,
  idPrefix = "date-picker-calendar",
  inline = false,
  allowPastDates = false,
  disableFutureDates = false,
  minDate,
  maxDate,
}) => {
  // Always start with current month, not the selected date's month
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const calendarRef = useRef<HTMLDivElement>(null);

  // Parse selected date if provided
  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    // Try to parse formats like "18 September 1980" or "26 September 1969"
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date;
    }
    return null;
  };

  // Format date as "DD Month YYYY" (e.g., "18 September 1980")
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Open calendar at selected date month when available, otherwise current month
  useEffect(() => {
    if (isOpen) {
      const parsed = selectedDate ? parseDate(selectedDate) : null;
      if (parsed) {
        setCurrentMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
      } else {
        const now = new Date();
        setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
      }
    }
  }, [isOpen, selectedDate]);

  // Close calendar when clicking outside (only for non-inline mode)
  useEffect(() => {
    if (inline) return; // Skip click outside for inline mode
    
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, inline]);

  const handleDateSelect = (date: Date) => {
    const formatted = formatDate(date);
    onDateSelect(formatted);
    if (!inline) {
      onClose();
    }
  };

  const isDateSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    const parsed = parseDate(selectedDate);
    if (!parsed) return false;
    return (
      date.getDate() === parsed.getDate() &&
      date.getMonth() === parsed.getMonth() &&
      date.getFullYear() === parsed.getFullYear()
    );
  };

  if (!isOpen) return null;

  // Get first day of current month
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay(); // 0-based, Sunday first

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const todayForYear = new Date();
  const minYear = 1900;
  const maxYear = todayForYear.getFullYear();
  const yearOptions: number[] = [];
  for (let y = maxYear; y >= minYear; y -= 1) {
    yearOptions.push(y);
  }

  // Create array of dates for the month
  const monthDates: (Date | null)[] = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    monthDates.push(null);
  }

  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    monthDates.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
  }

  // Fill remaining cells to make 6 rows (42 cells total)
  while (monthDates.length < 42) {
    monthDates.push(null);
  }

  return (
    <div
      ref={calendarRef}
      style={{
        position: inline ? "relative" : "absolute",
        top: inline ? "auto" : "100%",
        left: inline ? "auto" : 0,
        marginTop: inline ? 0 : getSpacing("8px"),
        backgroundColor: colorTokens["Background/White"],
        border: inline ? "none" : `1px solid ${colorTokens["Borders/Light"]}`,
        borderRadius: getSpacing("12px"),
        padding: getSpacing("16px"),
        display: "flex",
        flexDirection: "column",
        gap: getSpacing("12px"),
        boxShadow: inline ? "none" : "0px 4px 12px rgba(0, 0, 0, 0.15)",
        zIndex: inline ? "auto" : 1000,
        minWidth: inline ? "auto" : "300px",
        width: inline ? "100%" : "auto",
      }}
      id={`${idPrefix}-container`}
    >
      {/* Month Navigation */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: getSpacing("8px"),
        }}
      >
        <button
          type="button"
          onClick={() => {
            const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
            setCurrentMonth(newDate);
          }}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: getSpacing("4px"),
            fontSize: "18px",
            color: colorTokens["Text/Black"],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "32px",
            height: "32px",
            borderRadius: getSpacing("4px"),
            transition: "background-color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colorTokens["Background/Greyish"];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          ←
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: getSpacing("8px"),
            flex: 1,
            justifyContent: "center",
          }}
        >
          <select
            value={currentMonth.getMonth()}
            onChange={(e) => {
              const nextMonth = Number(e.target.value);
              const newDate = new Date(currentMonth.getFullYear(), nextMonth, 1);
              setCurrentMonth(newDate);
            }}
            style={{
              borderRadius: getSpacing("8px"),
              border: `1px solid ${colorTokens["Borders/Light"]}`,
              padding: `${getSpacing("4px")} ${getSpacing("8px")}`,
              backgroundColor: colorTokens["Background/White"],
              ...getTypography("Body/Small"),
              color: colorTokens["Text/Black"],
            }}
          >
            {monthNames.map((name, index) => (
              <option key={name} value={index}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={currentMonth.getFullYear()}
            onChange={(e) => {
              const nextYear = Number(e.target.value);
              const newDate = new Date(nextYear, currentMonth.getMonth(), 1);
              setCurrentMonth(newDate);
            }}
            style={{
              borderRadius: getSpacing("8px"),
              border: `1px solid ${colorTokens["Borders/Light"]}`,
              padding: `${getSpacing("4px")} ${getSpacing("8px")}`,
              backgroundColor: colorTokens["Background/White"],
              ...getTypography("Body/Small"),
              color: colorTokens["Text/Black"],
            }}
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => {
            const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
            setCurrentMonth(newDate);
          }}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: getSpacing("4px"),
            fontSize: "18px",
            color: colorTokens["Text/Black"],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "32px",
            height: "32px",
            borderRadius: getSpacing("4px"),
            transition: "background-color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colorTokens["Background/Greyish"];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          →
        </button>
      </div>

      {/* Day Headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: getSpacing("8px"),
        }}
      >
        {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
          <div
            key={`day-${index}`}
            style={{
              ...getTypography("Body/Small"),
              color: colorTokens["Text/Light Grey"],
              textAlign: "center",
              fontWeight: typographyTokens["Body/Medium"].weight,
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: getSpacing("8px"),
        }}
      >
        {monthDates.slice(0, 42).map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} style={{ padding: getSpacing("8px") }} />;
          }
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          const isPast = allowPastDates ? false : dateOnly < today;
          const isFuture = disableFutureDates && dateOnly > today;
          const minDateOnly = minDate ? parseDate(minDate) : null;
          const maxDateOnly = maxDate ? parseDate(maxDate) : null;
          const toDayOnly = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
          const isBeforeMin = minDateOnly != null && dateOnly < toDayOnly(minDateOnly);
          const isAfterMax = maxDateOnly != null && dateOnly > toDayOnly(maxDateOnly);
          const isDisabled = isPast || isFuture || isBeforeMin || isAfterMax;
          const isSelected = isDateSelected(date);
          return (
            <button
              key={index}
              type="button"
              onClick={() => !isDisabled && handleDateSelect(date)}
              disabled={isDisabled}
              style={{
                padding: getSpacing("8px"),
                border: "none",
                borderRadius: getSpacing("4px"),
                backgroundColor: isSelected
                  ? colorTokens["Background/Light Blue"]
                  : isDisabled
                  ? colorTokens["Background/Greyish"]
                  : colorTokens["Background/White"],
                color: isSelected
                  ? colorTokens["Primary/Default"]
                  : isDisabled
                  ? colorTokens["Text/Light Grey"]
                  : isWeekend
                  ? colorTokens["Alert/Red/Dark"]
                  : colorTokens["Text/Black"],
                cursor: isDisabled ? "not-allowed" : "pointer",
                ...getTypography("Body/Medium"),
                fontWeight: isSelected || (!isDisabled && !isWeekend) ? typographyTokens["Body/Medium"].weight : 400,
                opacity: isDisabled ? 0.5 : 1,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!isDisabled && !isSelected) {
                  e.currentTarget.style.backgroundColor = colorTokens["Background/Greyish"];
                }
              }}
              onMouseLeave={(e) => {
                if (!isDisabled && !isSelected) {
                  e.currentTarget.style.backgroundColor = colorTokens["Background/White"];
                }
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Done Button - Only show in inline mode */}
      {inline && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: getSpacing("8px"),
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              backgroundColor: colorTokens["Primary/Default"],
              color: colorTokens["Background/White"],
              border: "none",
              borderRadius: getSpacing("8px"),
              padding: `${getSpacing("8px")} ${getSpacing("16px")}`,
              ...getTypography("Body/Medium"),
              fontWeight: typographyTokens["Body/Medium"].weight,
              cursor: "pointer",
            }}
            id={`${idPrefix}-done-button`}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default DatePickerCalendar;
